import { omitBy } from "lodash";
import QueryString from 'qs';
import flowDrivers from "@/flows/driver";
import {
  preConfigWithCampaign,
  preConfigWithCartId,
} from '@/views/config/coupon/helper/pre-config';
import ComposeLoader from '@/common/compose-loader';
import { isEmptyVal } from '@/utils';
import { logger } from '@/utils/log';
import { ABANDON_CART_EMAIL_COOKIE_KEY, planTypes } from "../constants";
import { $cookie } from '@/common/utils'
import { preConfigWithPartnerRefer } from '@/views/config/partner-refer/helper/pre-config';
import { getPlanTypeByCategory } from "./parse";

export const flowTypes = {
  buyNow: 'buyNow',
  basic2pro: 'basic2pro',
  edit: 'edit',
  add2cart: 'add2cart',
  pro2biz: 'pro2biz',
  downgrade: 'downgrade',
  upgrade: 'upgrade',
  trial2paid: 'trial2paid',
  // BUY TRIAL
  buyTrial: 'buy_trial',
  // FREE TRIAL
  add2trial: 'add_trial',
  // maybe exist edit base and add addon
  mixBuy: 'mix_buy',
  pro2bundle: 'pro2zone_bundle',
  // biz to biz plus
  biz2bundle: 'biz2zone_bundle',
  // pro plus to biz plus
  prop2bundle: 'prop2zone_bundle',
  // pro to pro plus
  pro2prop: 'pro2zone_proplus',
  // zs to ze
  zs2ze: 'zs2ze',
  switch: 'switch',
  esc2escp: 'esc2escp',
  // biz to pro
  biz2pro: 'biz2pro',
  edu2esc: 'edu2esc',
  pbx2prop: 'pbx2prop',
  align: 'align',
  switchCurrency: 'switch_currency',
  upsell: 'upsell',
};

export const FLOWS_SHOULD_CLEAR_COMPANY = [ flowTypes.buyNow, flowTypes.basic2pro ]

function setScheduledEmailCookie(queryObj) {
  function isValidSCID(scID) {
    return (
      !!scID && scID !== 'null' && scID !== 'undefined'
    );
  }
  const queryScids = (queryObj.scid || '').split(',').filter(isValidSCID);
  const oldScIds = ($cookie.get(ABANDON_CART_EMAIL_COOKIE_KEY) || '').split(',').filter(isValidSCID);
  const nextScIds = [...new Set(queryScids.concat(oldScIds))].join(',');
  if (isValidSCID(nextScIds)) {
    $cookie.set(ABANDON_CART_EMAIL_COOKIE_KEY, nextScIds);
  }
}

// This function is used to save the refCode into session storage
function setRefCodeToSessionStorage(queryObj) {
  // We can't use "referralCode" URL variable since it's already used for partner referral
  const refCode = queryObj.refCode;
  if (refCode && refCode !== 'null' && refCode !== 'undefined') {
    sessionStorage.setItem('refCode', refCode);
  }
}

export function removeRefCodeFromSessionStorage() {
  sessionStorage.removeItem('refCode');
}

export const getDefaultFlowType = (originType) => {
  logger.info("origin flow type: ", originType);
  const isLoggedIn = window.SB?.loggedIn;

  // For net new user coming with type=basic2pro param, override the flowType to buyNow
  if (originType === flowTypes.basic2pro && !isLoggedIn) {
    logger.info("updated flow type", flowTypes.buyNow);
    return flowTypes.buyNow;
  }

  if (!isEmptyVal(originType)) return originType;

  if(isLoggedIn) {
    logger.info("updated flow type", flowTypes.basic2pro);
    return flowTypes.basic2pro;
  }

  logger.info("updated flow type", flowTypes.buyNow);
  return flowTypes.buyNow;
};

const pageConfigLoader = () => {
  const queryObj = QueryString.parse(location.search.slice(1));
  setScheduledEmailCookie(queryObj);
  setRefCodeToSessionStorage(queryObj);
  // for campaign link with marketing coupon
  const billingConfigLoader = new ComposeLoader(window.newBillingConfig);
  billingConfigLoader.decorate("preConfigWithPartnerRefer", preConfigWithPartnerRefer);
  billingConfigLoader.decorate("preConfigWithCampaign", preConfigWithCampaign);
  billingConfigLoader.decorate("preConfigWithCartId", preConfigWithCartId);
  const newBillingConfig = billingConfigLoader.dispatch();

  const globalPageConfig = { ...omitBy(newBillingConfig.pageConfig, isEmptyVal), ...queryObj };
  // if the origin flow type is empty, should set a default flow type with login status.
  globalPageConfig.type = getDefaultFlowType(globalPageConfig.type);
  // BUY TRIAL - user can visit buy trial page if not log in.
  const FLOW_TYPES_NOT_LOGGED_USER_CAN_VISIT = [flowTypes.buyNow, flowTypes.buyTrial];
  const isLoggedIn = window.SB?.loggedIn || FLOW_TYPES_NOT_LOGGED_USER_CAN_VISIT.includes(globalPageConfig.type);

  if(!isLoggedIn) {
    window.location.replace('/signin')
    return
  }

  const driver = flowDrivers.find((item) => item.test(globalPageConfig));

  const pageConfigState = { editConfig: {}, ...driver?.config({
    ...globalPageConfig,
    ...newBillingConfig,
  })};

  const targetCategory = globalPageConfig.plan;

  if (getPlanTypeByCategory(targetCategory) === planTypes.ZONE_EDU_PLUS || targetCategory === 'zoneduschcmp') {
    logger.info("re-set isZoomWorkplaceRebranding", window.newBillingConfig.isZoomWorkplaceEDURebranding);
    window.newBillingConfig.isZoomWorkplaceRebranding = window.newBillingConfig.isZoomWorkplaceEDURebranding
  }

  const config = {
    ...globalPageConfig,
    ...newBillingConfig,
    ...pageConfigState,
    isZoomWorkplaceRebranding: window.newBillingConfig.isZoomWorkplaceRebranding,
    isFromAbandonEmail: queryObj.stid
  };

  if (config.from === 'email') {
    // The email share link will include a `from=email` parameter, which is invalid, so we need to remove it.
    config.from = '';
  }

  return { config };
};

const configs = pageConfigLoader();

/** @type {GlobalPageConfigBean} */
export const pageConfig = window.pageConfig = configs.config;
